import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  BarChart3,
  PieChart as PieChartIcon,
  TrendingUp,
  Calendar,
  Building2,
  Wrench,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileText,
  Filter,
  CircleDollarSign
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";
import { format, subDays, eachDayOfInterval, parseISO, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { ar } from "date-fns/locale";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { IBMPlexSansArabicRegular } from "../fonts/IBMPlexSansArabicFont";
import { LucentLogoBase64 } from "../fonts/LucentLogoBase64";

const Reports = () => {
  const { token, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const reportRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/maintenance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/statistics`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("فشل في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Filter requests by date range
  const filteredRequests = requests.filter(r => {
    const requestDate = new Date(r.created_at);
    const from = startOfDay(parseISO(fromDate));
    const to = endOfDay(parseISO(toDate));
    return isWithinInterval(requestDate, { start: from, end: to });
  });

  // Status distribution
  const statusData = [
    { name: "قيد الانتظار", value: filteredRequests.filter(r => r.status === "pending").length, color: "#f59e0b" },
    { name: "مجدول", value: filteredRequests.filter(r => r.status === "scheduled").length, color: "#3b82f6" },
    { name: "قيد التنفيذ", value: filteredRequests.filter(r => r.status === "in_progress").length, color: "#8b5cf6" },
    { name: "مكتمل", value: filteredRequests.filter(r => r.status === "completed").length, color: "#10b981" },
  ].filter(d => d.value > 0);

  // Priority distribution
  const priorityData = [
    { name: "عاجل", value: filteredRequests.filter(r => r.priority === "urgent").length, color: "#ef4444" },
    { name: "عالي", value: filteredRequests.filter(r => r.priority === "high").length, color: "#f97316" },
    { name: "متوسط", value: filteredRequests.filter(r => r.priority === "medium").length, color: "#eab308" },
    { name: "منخفض", value: filteredRequests.filter(r => r.priority === "low").length, color: "#22c55e" },
  ].filter(d => d.value > 0);

  // Category distribution
  const categoryLabels = {
    wall: "الجدران", floor: "الأرضيات", ceiling: "السقف", lighting: "الإضاءة",
    plumbing: "السباكة", electrical: "الكهرباء", dental_chair: "كرسي الأسنان",
    xray: "جهاز الأشعة", sterilizer: "جهاز التعقيم", compressor: "الضاغط",
    suction: "جهاز الشفط", computer: "الكمبيوتر", printer: "الطابعة",
    network: "الشبكة", phone_system: "نظام الهاتف", other: "أخرى"
  };

  const categoryStats = {};
  filteredRequests.forEach(r => {
    const cat = r.category || "other";
    categoryStats[cat] = (categoryStats[cat] || 0) + 1;
  });

  const categoryData = Object.entries(categoryStats)
    .map(([key, value]) => ({
      name: categoryLabels[key] || key,
      value: value
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Element type distribution
  const elementTypeData = [
    { name: "إنشائي", value: filteredRequests.filter(r => r.element_type === "structural").length, color: "#6366f1" },
    { name: "أجهزة طبية", value: filteredRequests.filter(r => r.element_type === "device").length, color: "#ec4899" },
    { name: "تقنية معلومات", value: filteredRequests.filter(r => ["it", "IT"].includes(r.element_type)).length, color: "#14b8a6" },
  ].filter(d => d.value > 0);

  // Daily trend
  const dateRangeDays = eachDayOfInterval({
    start: parseISO(fromDate),
    end: parseISO(toDate)
  }).slice(-14); // Last 14 days of range

  const dailyTrend = dateRangeDays.map(day => {
    const dayRequests = requests.filter(r => {
      const requestDate = new Date(r.created_at);
      return requestDate.toDateString() === day.toDateString();
    });
    return {
      date: format(day, "MM/dd"),
      day: format(day, "EEEE", { locale: ar }),
      طلبات: dayRequests.length,
      مكتملة: dayRequests.filter(r => r.status === "completed").length
    };
  });

  // Calculate metrics
  const totalRequests = filteredRequests.length;
  const completedRequests = filteredRequests.filter(r => r.status === "completed").length;
  const completionRate = totalRequests > 0 ? Math.round((completedRequests / totalRequests) * 100) : 0;
  const urgentPending = filteredRequests.filter(r => r.priority === "urgent" && r.status !== "completed").length;

  // Calculate average completion days
  const completedWithDates = filteredRequests.filter(r => r.status === "completed" && r.created_at && r.updated_at);
  const avgResponseDays = completedWithDates.length > 0
    ? (completedWithDates.reduce((sum, r) => {
        const created = new Date(r.created_at);
        const updated = new Date(r.updated_at);
        const diffDays = (updated - created) / (1000 * 60 * 60 * 24);
        return sum + Math.max(diffDays, 0);
      }, 0) / completedWithDates.length).toFixed(1)
    : "0";

  // Expense metrics
  const requestsWithExpense = filteredRequests.filter(r => r.expense?.has_purchases);
  const totalEstimatedCost = requestsWithExpense.reduce((sum, r) => sum + (r.expense?.estimated_cost || 0), 0);
  const totalActualCost = requestsWithExpense.reduce((sum, r) => sum + (r.expense?.invoice?.actual_amount || 0), 0);
  const expenseCount = requestsWithExpense.length;
  const invoiceUploadedCount = requestsWithExpense.filter(r => r.expense?.status === "invoice_uploaded").length;

  const COLORS = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#3b82f6'];

  // Generate PDF Report
  const generatePDF = async () => {
    setGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Add IBM Plex Sans Arabic font
      doc.addFileToVFS("IBMPlexSansArabic.ttf", IBMPlexSansArabicRegular);
      doc.addFont("IBMPlexSansArabic.ttf", "IBMPlex", "normal");
      doc.setFont("IBMPlex", "normal");

      const tableStyles = { font: "IBMPlex", fontSize: 8, halign: "center", cellPadding: 2 };
      const headerStyle = { fillColor: [101, 93, 198], textColor: 255, font: "IBMPlex", fontSize: 8, halign: "center", cellPadding: 2 };

      // === HEADER ===
      doc.setFillColor(101, 93, 198);
      doc.rect(0, 0, 210, 28, 'F');

      // Logo on the right (original ratio 776:532 = 1.46:1)
      try { doc.addImage(LucentLogoBase64, 'PNG', 172, 4, 29, 20); } catch(e) {}

      // LUCENT Dental Center on the left
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text("LUCENT Dental Center", 15, 16, { align: "left" });

      // Date range below header
      doc.setFontSize(8);
      doc.setTextColor(101, 93, 198);
      doc.text(`${fromDate}  -  ${toDate}`, 105, 34, { align: "center" });

      // === KEY METRICS TABLE ===
      doc.setFontSize(9);
      doc.setTextColor(101, 93, 198);
      doc.text("الإحصائيات الرئيسية", 105, 40, { align: "center" });

      autoTable(doc, {
        startY: 42,
        body: [
          [{ content: "الحالة", styles: headerStyle }, { content: "القيمة", styles: headerStyle }, { content: "المقياس", styles: headerStyle }],
          [completionRate >= 70 ? "جيد" : "تحسين", `${completionRate}%`, "نسبة الإنجاز"],
          [urgentPending === 0 ? "ممتاز" : "انتباه", urgentPending.toString(), "طلبات عاجلة معلقة"],
          ["معلومات", `${completedRequests} / ${totalRequests}`, "مكتملة / إجمالي"],
        ],
        styles: tableStyles,
        alternateRowStyles: { fillColor: [248, 248, 252] },
        margin: { left: 15, right: 15 }
      });

      // === STATUS + PRIORITY side by side ===
      let currentY = doc.lastAutoTable.finalY + 5;
      doc.setFontSize(9);
      doc.setTextColor(101, 93, 198);
      doc.text("توزيع الحالات", 155, currentY, { align: "center" });
      doc.text("توزيع الأولويات", 55, currentY, { align: "center" });

      // Status table (right side)
      const statusRows = statusData.length > 0
        ? statusData.map(s => [`${totalRequests > 0 ? Math.round((s.value / totalRequests) * 100) : 0}%`, s.value.toString(), s.name])
        : [["-", "0", "لا توجد بيانات"]];
      autoTable(doc, {
        startY: currentY + 2,
        body: [
          [{ content: "%", styles: headerStyle }, { content: "العدد", styles: headerStyle }, { content: "الحالة", styles: headerStyle }],
          ...statusRows
        ],
        styles: tableStyles,
        alternateRowStyles: { fillColor: [248, 248, 252] },
        margin: { left: 110, right: 15 }
      });

      // Priority table (left side)
      const priorityRows = priorityData.length > 0
        ? priorityData.map(p => [`${totalRequests > 0 ? Math.round((p.value / totalRequests) * 100) : 0}%`, p.value.toString(), p.name])
        : [["-", "0", "لا توجد بيانات"]];
      autoTable(doc, {
        startY: currentY + 2,
        body: [
          [{ content: "%", styles: headerStyle }, { content: "العدد", styles: headerStyle }, { content: "الأولوية", styles: headerStyle }],
          ...priorityRows
        ],
        styles: tableStyles,
        alternateRowStyles: { fillColor: [248, 248, 252] },
        margin: { left: 15, right: 110 }
      });

      // === CATEGORY TABLE ===
      currentY = Math.max(doc.lastAutoTable.finalY, currentY + 30) + 5;
      doc.setFontSize(9);
      doc.setTextColor(101, 93, 198);
      doc.text("الطلبات حسب الفئة", 155, currentY, { align: "center" });
      doc.text("توزيع نوع العناصر", 55, currentY, { align: "center" });

      // Category (right side)
      const catRows = categoryData.length > 0
        ? categoryData.slice(0, 6).map(c => [c.value.toString(), c.name])
        : [["0", "لا توجد بيانات"]];
      autoTable(doc, {
        startY: currentY + 2,
        body: [
          [{ content: "العدد", styles: headerStyle }, { content: "الفئة", styles: headerStyle }],
          ...catRows
        ],
        styles: tableStyles,
        alternateRowStyles: { fillColor: [248, 248, 252] },
        margin: { left: 110, right: 15 }
      });

      // Element type (left side)
      const elemRows = elementTypeData.length > 0
        ? elementTypeData.map(e => [`${totalRequests > 0 ? Math.round((e.value / totalRequests) * 100) : 0}%`, e.value.toString(), e.name])
        : [["-", "0", "لا توجد بيانات"]];
      autoTable(doc, {
        startY: currentY + 2,
        body: [
          [{ content: "%", styles: headerStyle }, { content: "العدد", styles: headerStyle }, { content: "النوع", styles: headerStyle }],
          ...elemRows
        ],
        styles: tableStyles,
        alternateRowStyles: { fillColor: [248, 248, 252] },
        margin: { left: 15, right: 110 }
      });

      // === LOCATION STATS ===
      currentY = Math.max(doc.lastAutoTable.finalY, currentY + 30) + 5;
      doc.setFontSize(9);
      doc.setTextColor(101, 93, 198);
      doc.text("إحصائيات المواقع", 105, currentY, { align: "center" });

      const locHeaders = [];
      const locValues = [];
      if (!user?.role || user.role === "admin" || user.role === "maintenance_manager") {
        locHeaders.push("المرافق", "دورات المياه", "المكاتب", "العيادات");
        locValues.push(stats?.total_facilities?.toString() || "0", stats?.total_restrooms?.toString() || "0", stats?.total_offices?.toString() || "0", stats?.total_clinics?.toString() || "0");
      } else if (user.role === "medical_manager") {
        locHeaders.push("العيادات");
        locValues.push(stats?.total_clinics?.toString() || "0");
      } else if (user.role === "it_manager") {
        locHeaders.push("المرافق", "المكاتب", "العيادات");
        locValues.push(stats?.total_facilities?.toString() || "0", stats?.total_offices?.toString() || "0", stats?.total_clinics?.toString() || "0");
      }

      autoTable(doc, {
        startY: currentY + 2,
        body: [
          locHeaders.map(h => ({ content: h, styles: headerStyle })),
          locValues
        ],
        styles: tableStyles,
        margin: { left: 15, right: 15 }
      });

      // === EXPENSE SUMMARY ===
      currentY = doc.lastAutoTable.finalY + 5;
      doc.setFontSize(9);
      doc.setTextColor(101, 93, 198);
      doc.text("ملخص المصاريف", 105, currentY, { align: "center" });

      autoTable(doc, {
        startY: currentY + 2,
        body: [
          [{ content: "الفواتير المرفوعة", styles: headerStyle }, { content: "المبلغ الفعلي", styles: headerStyle }, { content: "التكلفة التقديرية", styles: headerStyle }, { content: "طلبات بمشتريات", styles: headerStyle }],
          [`${invoiceUploadedCount} / ${expenseCount}`, `${totalActualCost.toLocaleString()} ريال`, `${totalEstimatedCost.toLocaleString()} ريال`, expenseCount.toString()]
        ],
        styles: tableStyles,
        margin: { left: 15, right: 15 }
      });

      // === FOOTER ===
      doc.setFont("IBMPlex", "normal");
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text("LUCENT Dental Center | Maintenance Report", 105, 288, { align: "center" });

      // Save PDF
      const fileName = `Maintenance_Report_${fromDate}_${toDate}.pdf`;
      doc.save(fileName);
      toast.success("تم تحميل التقرير بنجاح");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("فشل في إنشاء التقرير");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page" ref={reportRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6]">التقارير والإحصائيات</h1>
          <p className="text-slate-500 mt-1">تحليل شامل لطلبات الصيانة والأداء</p>
        </div>
        <Button 
          onClick={generatePDF} 
          disabled={generating}
          className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
          data-testid="generate-pdf-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin ml-2" />
          ) : (
            <FileText className="w-4 h-4 ml-2" />
          )}
          طباعة تقرير PDF
        </Button>
      </div>

      {/* Date Range Filter */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-end gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-[#655dc6]" />
              <span className="font-medium text-[#655dc6]">فلترة الفترة:</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="space-y-2">
                <Label>من تاريخ</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full sm:w-44"
                  data-testid="from-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>إلى تاريخ</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full sm:w-44"
                  data-testid="to-date-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setFromDate(format(subDays(new Date(), 7), "yyyy-MM-dd"));
                  setToDate(format(new Date(), "yyyy-MM-dd"));
                }}
              >
                آخر 7 أيام
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setFromDate(format(subDays(new Date(), 30), "yyyy-MM-dd"));
                  setToDate(format(new Date(), "yyyy-MM-dd"));
                }}
              >
                آخر 30 يوم
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setFromDate(format(subDays(new Date(), 90), "yyyy-MM-dd"));
                  setToDate(format(new Date(), "yyyy-MM-dd"));
                }}
              >
                آخر 3 أشهر
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-[#655dc6]">{totalRequests}</p>
                <p className="text-sm text-slate-500">إجمالي الطلبات</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{completionRate}%</p>
                <p className="text-sm text-slate-500">نسبة الإنجاز</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-red-600">{urgentPending}</p>
                <p className="text-sm text-slate-500">عاجل غير مكتمل</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{avgResponseDays}</p>
                <p className="text-sm text-slate-500">متوسط أيام الإنجاز</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expense Summary */}
      <Card className="border-0 shadow-sm bg-gradient-to-l from-[#F9F7FB] to-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-[#655dc6] flex items-center gap-2 text-lg">
            <CircleDollarSign className="w-5 h-5" />
            ملخص مصاريف الصيانة
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-white rounded-lg border">
              <p className="text-sm text-slate-500 mb-1">عدد الطلبات بمشتريات</p>
              <p className="text-2xl font-bold text-[#655dc6]">{expenseCount}</p>
            </div>
            <div className="p-4 bg-white rounded-lg border">
              <p className="text-sm text-slate-500 mb-1">إجمالي التكلفة التقديرية</p>
              <p className="text-2xl font-bold text-amber-600">{totalEstimatedCost.toLocaleString()} <span className="text-sm font-normal">ريال</span></p>
            </div>
            <div className="p-4 bg-white rounded-lg border">
              <p className="text-sm text-slate-500 mb-1">إجمالي المبلغ الفعلي</p>
              <p className="text-2xl font-bold text-emerald-600">{totalActualCost.toLocaleString()} <span className="text-sm font-normal">ريال</span></p>
            </div>
            <div className="p-4 bg-white rounded-lg border">
              <p className="text-sm text-slate-500 mb-1">الفواتير المرفوعة</p>
              <p className="text-2xl font-bold text-blue-600">{invoiceUploadedCount} <span className="text-sm font-normal text-slate-400">/ {expenseCount}</span></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              اتجاه الطلبات اليومي
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      textAlign: 'right'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="طلبات" stroke="#655dc6" strokeWidth={2} dot={{ fill: '#655dc6' }} />
                  <Line type="monotone" dataKey="مكتملة" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" />
              توزيع الحالات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              الطلبات حسب الفئة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#655dc6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Priority & Element Type */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              توزيع الأولويات والأنواع
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Priority */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-3 text-center">الأولوية</p>
                <div className="space-y-2">
                  {priorityData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Element Type */}
              <div>
                <p className="text-sm font-medium text-slate-600 mb-3 text-center">نوع العنصر</p>
                <div className="space-y-2">
                  {elementTypeData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Stats - filtered by role */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            إحصائيات المواقع
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <p className="text-3xl font-bold text-purple-600">{stats?.total_clinics || 0}</p>
              <p className="text-sm text-slate-600 mt-1">العيادات</p>
            </div>
            {user?.role !== "medical_manager" && (
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-3xl font-bold text-blue-600">{stats?.total_offices || 0}</p>
                <p className="text-sm text-slate-600 mt-1">المكاتب</p>
              </div>
            )}
            {!["medical_manager", "it_manager"].includes(user?.role) && (
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <p className="text-3xl font-bold text-emerald-600">{stats?.total_restrooms || 0}</p>
                <p className="text-sm text-slate-600 mt-1">دورات المياه</p>
              </div>
            )}
            {user?.role !== "medical_manager" && (
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <p className="text-3xl font-bold text-amber-600">{stats?.total_facilities || 0}</p>
                <p className="text-sm text-slate-600 mt-1">المرافق</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-[#655dc6]">ملخص الأداء</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-right py-3 px-4 font-medium text-slate-600">المقياس</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-600">القيمة</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-600">الحالة</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4">إجمالي الطلبات</td>
                  <td className="text-center py-3 px-4 font-medium">{totalRequests}</td>
                  <td className="text-center py-3 px-4">
                    <Badge className="bg-purple-100 text-purple-700">معلومات</Badge>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">نسبة الإنجاز</td>
                  <td className="text-center py-3 px-4 font-medium">{completionRate}%</td>
                  <td className="text-center py-3 px-4">
                    <Badge className={completionRate >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {completionRate >= 70 ? "جيد" : "يحتاج تحسين"}
                    </Badge>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">طلبات عاجلة معلقة</td>
                  <td className="text-center py-3 px-4 font-medium">{urgentPending}</td>
                  <td className="text-center py-3 px-4">
                    <Badge className={urgentPending === 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                      {urgentPending === 0 ? "ممتاز" : "يتطلب اهتمام"}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">متوسط وقت الإنجاز</td>
                  <td className="text-center py-3 px-4 font-medium">{avgResponseDays} يوم</td>
                  <td className="text-center py-3 px-4">
                    <Badge className={avgResponseDays <= 3 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {avgResponseDays <= 3 ? "سريع" : "متوسط"}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
